import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EntityPickerWithDefaultsProps,
  EntityPickerWithDefaultsSchema,
  PopulatedFieldUpdate,
} from './types';
import { extractValueFromEntityPath } from './utils';

export const EntityPickerWithDefaults = (props: EntityPickerWithDefaultsProps): JSX.Element | null => {
  const {
    onChange,
    schema,
    uiSchema,
    formData,
    formContext,
    required,
    disabled,
  } = props;

  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntityRef, setSelectedEntityRef] = useState<string>(formData || '');
  const [lastProcessedRef, setLastProcessedRef] = useState<string | null>(null);
  const processingRef = useRef<string | null>(null);

  const uiOptions = (uiSchema?.['ui:options'] || {}) as EntityPickerWithDefaultsSchema;
  const { catalogFilter, populateFields } = uiOptions;

  // Fetch entities based on catalog filter
  const fetchEntities = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const entitiesResponse = await catalogApi.getEntities({
        filter: catalogFilter || {},
      });

      setEntities(entitiesResponse.items || []);
    } catch (err) {
      setError('Failed to load entities');
    } finally {
      setIsLoading(false);
    }
  }, [catalogApi, catalogFilter]);

  // Handle entity selection and field population
  const handleEntityChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      const entityRef = event.target.value as string;
      setSelectedEntityRef(entityRef);
      onChange(entityRef);

      // If no entityRef or no field population configuration, we're done
      if (!entityRef || !populateFields || Object.keys(populateFields).length === 0) {
        setLastProcessedRef(entityRef);
        return;
      }

      // Prevent duplicate processing
      if (entityRef === lastProcessedRef || processingRef.current === entityRef) {
        return;
      }

      processingRef.current = entityRef;

      try {
        // Find the selected entity in our loaded entities or fetch it
        let entity = entities.find(e => stringifyEntityRef(e) === entityRef);

        if (!entity) {
          entity = await catalogApi.getEntityByRef(entityRef);
        }

        if (entity && processingRef.current === entityRef) {
          // Extract and populate dependent field values
          const fieldUpdates: PopulatedFieldUpdate[] = [];

          Object.entries(populateFields).forEach(([fieldName, entityPath]) => {
            try {
              const value = extractValueFromEntityPath(entity, entityPath);
              if (value !== undefined && value !== null && value !== '') {
                fieldUpdates.push({
                  fieldName,
                  value,
                });
              }
            } catch (_extractErr) {
              // Field extraction failed; skip this field
            }
          });

          if (fieldUpdates.length > 0) {
            // Attempt different methods to update form fields based on formContext structure
            if (formContext && typeof formContext === 'object') {
              // Method 1: Try updateFormData function if available
              if ('updateFormData' in formContext && typeof (formContext as any).updateFormData === 'function') {
                const updates = fieldUpdates.reduce(
                  (acc, { fieldName, value }) => ({
                    ...acc,
                    [fieldName]: value,
                  }),
                  {}
                );
                (formContext as any).updateFormData(updates);
              }
              // Method 2: Try direct formData mutation
              else if ('formData' in formContext && typeof (formContext as any).formData === 'object') {
                fieldUpdates.forEach(({ fieldName, value }) => {
                  if ((formContext as any).formData) {
                    ((formContext as any).formData as any)[fieldName] = value;
                  }
                });

                // Try to trigger change event
                if ('onChange' in formContext && typeof (formContext as any).onChange === 'function') {
                  (formContext as any).onChange((formContext as any).formData);
                }
              }
            }
          }

          setLastProcessedRef(entityRef);
        }
      } catch (_populateErr) {
        // Entity population failed; selection still recorded via onChange above
      } finally {
        if (processingRef.current === entityRef) {
          processingRef.current = null;
        }
      }
    },
    [onChange, catalogApi, populateFields, formContext, lastProcessedRef, entities]
  );

  // Initialize entities on mount
  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  // Initialize with existing form data if available
  useEffect(() => {
    if (formData && formData !== lastProcessedRef && !processingRef.current && entities.length > 0) {
      // Create a synthetic event to trigger the handler
      const syntheticEvent = {
        target: { value: formData }
      } as React.ChangeEvent<{ value: unknown }>;
      handleEntityChange(syntheticEvent);
    }
  }, [formData, lastProcessedRef, handleEntityChange, entities]);

  // Sort entities alphabetically by title
  const sortedEntities = [...entities].sort((a, b) => {
    // For groups and other entities, prefer displayName/title over name
    // Groups use spec.profile.displayName, while other entities use metadata.title
    const rawTitleA = (
      (a.spec as any)?.profile?.displayName ||
      a.metadata?.title ||
      a.metadata?.name ||
      ''
    );
    const rawTitleB = (
      (b.spec as any)?.profile?.displayName ||
      b.metadata?.title ||
      b.metadata?.name ||
      ''
    );

    // Transform titles for comparison (replace underscores/hyphens with spaces)
    const titleA = rawTitleA.replace(/[_-]/g, ' ').toLowerCase();
    const titleB = rawTitleB.replace(/[_-]/g, ' ').toLowerCase();

    return titleA.localeCompare(titleB);
  });

  return (
    <FormControl margin="normal" required={required} error={!!error} fullWidth disabled={disabled}>
      <InputLabel id="entity-picker-with-defaults-label">
        {schema.title || 'Select Entity'}
      </InputLabel>
      <Select
        labelId="entity-picker-with-defaults-label"
        value={selectedEntityRef}
        onChange={handleEntityChange}
        disabled={isLoading || disabled}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {sortedEntities.map((entity) => {
          const entityRef = stringifyEntityRef(entity);

          // Get the raw display value
          const rawDisplayValue = (
            (entity.spec as any)?.profile?.displayName ||
            entity.metadata?.title ||
            entity.metadata?.name
          );

          // Transform the display title:
          // - If it's a technical name (e.g., "parts_data_team"), make it readable
          // - Replace underscores and hyphens with spaces
          // - Capitalize each word
          const displayTitle = rawDisplayValue
            ? rawDisplayValue
              .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
              .split(' ')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
            : entityRef;

          return (
            <MenuItem key={entityRef} value={entityRef}>
              {displayTitle}
            </MenuItem>
          );
        })}
      </Select>
      {isLoading && (
        <FormHelperText>
          <CircularProgress size={16} style={{ marginRight: 8 }} />
          Loading entities...
        </FormHelperText>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!error && !isLoading && (
        <FormHelperText>
          {populateFields ? 'Select an entity to auto-populate related fields' : 'Select an entity from the catalog'}
        </FormHelperText>
      )}
    </FormControl>
  );
};